import { IoSearchOutline } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import React, { useContext, useEffect, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import Heading from '../../components/shared/heading';
import VehicleTable from './vehicle-table';
import { getAllVehicleBodies, getAllVehicleMakes, getAllVehicles, getAllVehicleStatuses, getAllVehicleTypes } from '../../services/vehicles-service';
import { AccountsContext } from '../../components/context/account-context';
import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import logo from '../../assets/images/logo.jpg';
import { CSVLink } from 'react-csv';
import Modal from '../../components/shared/modal';
import AddModal from './add-modal';
import Help from './help';
import { usePageHelp } from '../../components/context/help-context';

const Vehicles = () => {
    usePageHelp(Help);
    const { accountId, attributes } = useContext(AccountsContext);
    const [vehicleData, setVehicleData] = useState([]);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [newlyAddedItem, setNewlyAddedItem] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [globalDataLoading, setGlobalDataLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [vehicleBodies, setVehicleBodies] = useState([]);
    const [vehicleMakes, setVehicleMakes] = useState([]);
    const [vehicleStatuses, setVehicleStatuses] = useState([]);
    const [fetchingGlobalData, setFetchingGlobalData] = useState(false);
    const [statusesLoading, setStatusesLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});

    const fetchGlobalData = async () => {
        if (!accountId) return;
        setGlobalDataLoading(true);
        setFetchingGlobalData(true);
        try {
            const [vehicleTypes, vehicleBodies, vehicleMakes] = await Promise.all([
                getAllVehicleTypes(accountId),
                getAllVehicleBodies(accountId),
                getAllVehicleMakes(accountId),
            ]);

            if (vehicleTypes.success) setVehicleTypes(vehicleTypes.data.data || []);
            if (vehicleBodies.success) setVehicleBodies(vehicleBodies.data.data || []);
            if (vehicleMakes.success) setVehicleMakes(vehicleMakes.data.data || []);
            setError(null);
        } catch (err) {
            console.error("Failed to fetch global data", err);
            setError('Failed to fetch global data');
        } finally {
            setGlobalDataLoading(false);
            setFetchingGlobalData(false);
        }
    };

    const fetchVehicleStatuses = async () => {
        setStatusesLoading(true);
        try {
            // Fetch both vehicles and statuses in parallel
            const [statusesResponse] = await Promise.all([
                getAllVehicleStatuses(accountId)
            ]);

            if (statusesResponse.success) {
                setVehicleStatuses(statusesResponse.data.data || []);
            } else {
                console.error('Failed to fetch vehicle statuses:', statusesResponse.data.message);
            }
        } catch (err) {
            console.error("Error fetching vehicles and statuses:", err);
            setError('An error occurred while fetching vehicle records');
        } finally {
            setStatusesLoading(false);
        }
    };

    const fetchVehicles = async () => {
        setLoading(true);
        try {
            // Fetch both vehicles and statuses in parallel
            const [vehiclesResponse] = await Promise.all([
                getAllVehicles(accountId),
            ]);

            if (vehiclesResponse.success) {
                const sortedData = vehiclesResponse.data.data.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn));
                setVehicleData(sortedData || []);
                setError(null);
            } else {
                setError(vehiclesResponse.data.message || 'Failed to fetch vehicles records');
            }
        } catch (err) {
            console.error("Error fetching vehicles and statuses:", err);
            setError('An error occurred while fetching vehicle records');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicles();
    }, [accountId]);

    const handleOpenAddModal = async () => {
        try {
            setOpenAddModal(true);
            if (vehicleTypes.length === 0 || vehicleBodies.length === 0 || vehicleMakes.length === 0) {
                await fetchGlobalData();
            }
        } catch (error) {
            console.error("Failed to fetch global data", error);
            setError('Failed to fetch required data for adding a vehicle');
        }
        setOpenAddModal(true);
    };

    const handleCloseAddModal = () => {
        setOpenAddModal(false);
    };

    const handleAddItem = (newItem) => {
        setNewlyAddedItem(newItem);
        fetchVehicles();
    };

    const handleOpenEditModal = (item) => {
        setNewlyAddedItem(item);
    };

    const handleClearNewlyAddedItem = () => {
        setNewlyAddedItem(null);
    };


    // Filter data for export based on selected rows
    const exportData = Object.keys(selectedRows).length > 0
        ? vehicleData.filter((_, index) => selectedRows[index])
        : vehicleData;

    // CSV export
    const csvData = exportData.map(item => ({
        Name: item.name || '',
        'License Plate': item.licensePlate || '',
        Year: item.year || '',
        Make: item.make || '',
        Model: item.model || '',
        'Vehicle Type': item.vehicleType || '',
        Status: item.status || '',
    }));

    // PDF export
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Logo placement
        const logoWidth = 25;
        const logoHeight = 15;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = 2;

        doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

        // Title
        const title = 'Vehicle Report';
        const titleY = logoY + logoHeight + 5;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        const titleWidth = doc.getTextWidth(title);
        const titleX = (pageWidth - titleWidth) / 2;
        doc.text(title, titleX, titleY);

        // Table
        autoTable(doc, {
            head: [['Name', 'License Plate', 'Year', 'Make', 'Model', 'Vehicle Type', 'Status']],
            body: exportData.map(item => [
                item.name || '',
                item.licensePlate || '',
                item.year || '',
                item.make || '',
                item.model || '',
                item.vehicleType || '',
                item.status || '',
            ]),
            startY: titleY + 5,
        });

        // File name
        const fileName = `vehicles.pdf`;
        doc.save(fileName);
    };

    const isExportDisabled = Object.keys(selectedRows).length === 0;

    return (
        <div className="px-6 py-4">
            <div className='flex mb-2 items-center justify-between'>
                <div className=''>
                    <Heading>Vehicles</Heading>
                </div>
                <Button
                    label='Add Vehicle'
                    onClick={handleOpenAddModal}
                    className='text-white px-4 !py-1 rounded-lg bg-btnBg'
                    disabled={globalDataLoading}
                />
            </div>

            <div className='space-y-4'>
                {error && (
                    <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                        {error}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex gap-3 flex-wrap">
                        <div className="bg-white rounded-md px-3 flex border border-border items-center gap-2 w-44 lg:w-72 py-1">
                            <IoSearchOutline className="text-gray-500 text-lg" />
                            <input
                                type="text"
                                className="bg-transparent outline-none py-1 w-full text-sm text-gray-700"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 items-center">
                        <Button
                            onClick={handleExportPDF}
                            className={` ${isExportDisabled ? '!cursor-not-allowed !opacity-75' : 'hover:!bg-gray-50'} flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md `}
                            label="PDF"
                            icon={<FiDownload className="text-gray-600" />}
                            iconPosition='left'
                            disabled={isExportDisabled}
                        />
                        <div
                            className={`flex items-center gap-1 !text-sm !px-3 !py-1.5 border rounded-md ${isExportDisabled ? 'cursor-not-allowed opacity-75' : 'hover:bg-gray-50'}`}
                        >
                            <CSVLink
                                data={csvData}
                                onClick={(event) => {
                                    if (isExportDisabled) {
                                        event.preventDefault();
                                    }
                                }}
                                filename={'Vehicles'}
                                className={`${isExportDisabled ? 'cursor-not-allowed' : ''} flex items-center gap-1`}
                            >
                                <FiDownload className="text-gray-600" />
                                CSV
                            </CSVLink>
                        </div>
                    </div>
                </div>

                <VehicleTable
                    data={vehicleData}
                    globalFilter={searchTerm}
                    onGlobalFilterChange={setSearchTerm}
                    setData={setVehicleData}
                    onClearNewlyAddedItem={handleClearNewlyAddedItem}
                    fetchVehicles={fetchVehicles}
                    loadingData={loading}
                    accountId={accountId}
                    vehicleStatuses={vehicleStatuses}
                    vehicleTypes={vehicleTypes}
                    vehicleBodies={vehicleBodies}
                    vehicleMakes={vehicleMakes}
                    globalDataLoading={globalDataLoading}
                    fetchGlobalData={fetchGlobalData}
                    fetchVehicleStatuses={fetchVehicleStatuses}
                    statusesLoading={statusesLoading}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                />

                <Modal
                    isOpen={openAddModal}
                    onClose={handleCloseAddModal}
                    modalClass={'xl:w-[40%] lg:w-[45%] md:w-[65%] sm:w-[70%] w-[90%] !px-0'}
                >
                    <AddModal
                        handleCloseAddModal={handleCloseAddModal}
                        onAdd={handleAddItem}
                        openEditModal={handleOpenEditModal}
                        vehicleTypes={vehicleTypes}
                        vehicleBodies={vehicleBodies}
                        vehicleMakes={vehicleMakes}
                        vehicleStatuses={vehicleStatuses}
                        globalDataLoading={globalDataLoading || fetchingGlobalData}
                    />
                </Modal>

            </div>
        </div>
    );
};

export default Vehicles;